import { useState, useCallback, useRef } from "react";
import { get } from "lodash";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import {
  ExportStartEventArgs,
  ImportSuccessEventArgs,
  IPoint,
  IStampAnnotation,
  PdfAnnotationBase,
  PdfViewerComponent,
  PdfViewerModel,
} from "@syncfusion/ej2-react-pdfviewer";
import { usePdfTemplateExpression } from "./usePdfTemplateExpression";
import { ExpressionData, AnnotationDefaults } from "../types/types";

export interface PdfTemplateDesignerModule {
  // State
  pdfViewerId: string | null;
  isReadOnlyMode: boolean | null;
  isSaving: boolean | null;
  hasAnnotationsModified: boolean;

  // Methods
  addExpressionAnnotation: (
    templateExpression: ExpressionData,
    point: IPoint
  ) => PdfAnnotationBase | undefined;
  save: () => Promise<void>;
  importAnnotations: (annotationsData?: string) => Promise<void>;
  setDesignMode: () => void;
  setPreviewMode: (previewFormData?: Record<string, string>) => Promise<void>;
  setup: (args: {
    pdfViewerId: string;
    isReadOnlyMode: boolean;
  }) => PdfViewerModel & DefaultHtmlAttributes;
  initialize: (fileFormData?: string) => Promise<void>;
  reset: () => void;
  setPdfViewerId: (pdfViewerId: string) => void;
}

export const usePdfTemplateDesigner = (
  pdfViewerRef: React.RefObject<PdfViewerComponent | null>,
  onSave?: (fileFormData?: string) => Promise<void>,
  onPreviewAnnotations?: (data?: any, annotationsData?: string) => void,
  getTemplateFieldsData?: (
    formId?: string
  ) => Promise<Record<string, string> | {}>,
  annotationDefaults?: AnnotationDefaults
): PdfTemplateDesignerModule => {
  // State
  const [pdfViewerId, setPdfViewerIdState] = useState<string | null>(null);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState<boolean | null>(null);
  const [isSaving, setIsSaving] = useState<boolean | null>(null);
  const [hasAnnotationsModified, setHasAnnotationsModified] =
    useState<boolean>(false);

  // Use the expression module
  const expressionsModule = usePdfTemplateExpression(
    pdfViewerRef,
    onPreviewAnnotations,
    annotationDefaults
  );

  // Default settings ref
  const defaultPdfViewerSettings = useRef<
    PdfViewerModel & DefaultHtmlAttributes
  >({
    className: "e-control e-lib e-pdfviewer",
    enableThumbnail: true,
    isThumbnailViewOpen: true,
    enableNavigationToolbar: true,
    enableBookmark: false,
    enableCommentPanel: false,
    enablePrint: false,
    enableToolbar: false,
    annotationAdd: () => {
      setHasAnnotationsModified(true);
    },
    annotationRemove: () => {
      setHasAnnotationsModified(true);
    },
    annotationResize: () => {
      setHasAnnotationsModified(true);
    },
    toolbarSettings: {
      toolbarItems: [],
      annotationToolbarItems: [
        "AnnotationDeleteTool",
        "FontFamilyAnnotationTool",
        "FontSizeAnnotationTool",
        "FontStylesAnnotationTool",
        "FontAlignAnnotationTool",
        "FontColorAnnotationTool",
        "ColorEditTool",
        "StrokeColorEditTool",
        "ThicknessEditTool",
        "OpacityEditTool",
      ],
      formDesignerToolbarItems: [],
    },
  });

  // Methods
  const addExpressionAnnotation = useCallback(
    (
      templateExpression: ExpressionData,
      point: IPoint
    ): PdfAnnotationBase | undefined => {
      if (!pdfViewerRef.current) {
        return undefined;
      }

      return expressionsModule.addExpressionAnnotation(
        templateExpression,
        point
      );
    },
    [pdfViewerRef.current, expressionsModule]
  );

  const save = useCallback(async () => {
    setIsSaving(true);

    // Get Annotations data
    const annotationsData = await expressionsModule.exportAnnotations();

    // Trigger event
    if (onSave) {
      await onSave(annotationsData);
    }

    setIsSaving(false);
    setHasAnnotationsModified(false);
  }, [expressionsModule, onSave]);

  const importAnnotations = useCallback(
    async (annotationsData?: string) => {
      if (annotationsData) {
        await expressionsModule.importAnnotations(annotationsData);
      }
    },
    [expressionsModule]
  );

  const setDesignMode = useCallback(() => {
    if (!pdfViewerRef.current) {
      return;
    }

    // Transform Annotations
    if (pdfViewerRef.current.annotations.length <= 0) {
      //"No Annotations"
      return;
    }

    expressionsModule.designAnnotations();
  }, [pdfViewerRef.current, expressionsModule]);

  const setPreviewMode = useCallback(
    async (previewFormData?: Record<string, string>) => {
      if (!pdfViewerRef.current) {
        return;
      }

      // Transform Annotations
      if (pdfViewerRef.current.annotationCollection.length <= 0) {
        // No Annotations
        return;
      }

      if (!previewFormData && getTemplateFieldsData) {
        previewFormData = await getTemplateFieldsData?.();
      }

      await expressionsModule.previewAnnotations(previewFormData || undefined);
    },
    [pdfViewerRef.current, expressionsModule, getTemplateFieldsData]
  );

  const handleExportStart = useCallback(
    (args: ExportStartEventArgs) => {
      const stampAnnotations: IStampAnnotation[][] = JSON.parse(
        args.exportData.stampAnnotations
      );

      const newStampAnnotations = stampAnnotations.map((page) =>
        page.map((annotation) => {
          const foundOgAnnotation =
            expressionsModule.originalImportedData?.find(
              (ogAnnotation) => ogAnnotation.AnnotName === annotation.annotName
            );
          const foundOgAnnotationImageData = get(
            foundOgAnnotation?.Apperarance.find(
              (apperarance) => apperarance["imagedata"]
            ),
            "imagedata"
          );

          const isUnloadedStamped =
            annotation.stampAnnotationType === "path" &&
            !!annotation.customData;

          if (isUnloadedStamped) {
            return {
              ...annotation,
              stampAnnotationType: "image",
              stampAnnotationPath:
                foundOgAnnotationImageData ?? annotation.stampAnnotationPath,
            };
          } else {
            return annotation;
          }
        })
      );

      args.exportData.stampAnnotations = JSON.stringify(newStampAnnotations);
    },
    [expressionsModule.originalImportedData]
  );

  const setup = useCallback(
    (args: {
      pdfViewerId: string;
      isReadOnlyMode: boolean;
    }): PdfViewerModel & DefaultHtmlAttributes => {
      // Setup Properties
      setPdfViewerIdState(args.pdfViewerId);
      setIsReadOnlyMode(args.isReadOnlyMode);

      // Setup Settings
      const settings = {
        ...defaultPdfViewerSettings.current,
      } as PdfViewerModel & DefaultHtmlAttributes;

      if (args.isReadOnlyMode) {
        settings.toolbarSettings!.annotationToolbarItems = [];
      }

      settings.id = args.pdfViewerId;

      settings.exportStart = handleExportStart;

      // Events
      if (!args.isReadOnlyMode) {
        settings.annotationSelect = expressionsModule.handleAnnotationSelect;
        settings.annotationUnSelect =
          expressionsModule.handleAnnotationUnselect;
        settings.annotationRemove = expressionsModule.annotationRemove;
      }

      // Export Start
      // Fix to remove the annotations that are not defined
      settings.exportStart = (args: ExportStartEventArgs) => {
        const annts = JSON.parse(args.exportData.annotationCollection);
        const filteredAnnts = annts.filter((a: any) => a.annotationAddMode);
        args.exportData.annotationCollection = JSON.stringify(filteredAnnts);
      };

      settings.importSuccess = (args: ImportSuccessEventArgs) => {
        if (args?.importData) {
          const importedData = (
            Object.values(args.importData)
              .map((x: any) => Object.values(x))
              .flat() as any[]
          )
            .filter((x) => x.length > 0)
            .flat();

          // Set Imported Data
          expressionsModule.updateOriginalImportedData(importedData);
        }
      };

      // Expressions
      expressionsModule.setup({ pdfViewerId: args.pdfViewerId });

      return settings;
    },
    [handleExportStart, expressionsModule]
  );

  const initialize = useCallback(
    async (fileFormData?: string) => {
      // Import Annotations
      await importAnnotations(fileFormData);
    },
    [importAnnotations]
  );

  const reset = useCallback(() => {
    setHasAnnotationsModified(false);
  }, [expressionsModule]);

  const setPdfViewerId = useCallback((pdfViewerId: string) => {
    setPdfViewerIdState(pdfViewerId);
  }, []);

  return {
    // State
    pdfViewerId,
    isReadOnlyMode,
    isSaving,
    hasAnnotationsModified,

    // Methods
    addExpressionAnnotation,
    save,
    importAnnotations,
    setDesignMode,
    setPreviewMode,
    setup,
    initialize,
    reset,
    setPdfViewerId,
  };
};
